'use client'

/**
 * The confirmation step of the in-app form: preview a chosen catalogue result, edit it, confirm.
 *
 * Out of `src/components/library/media-preview-card.tsx` on #405. It sat in a directory named for
 * the library and had no library consumer — every importer is a capture-form file, and the domain
 * table gives `capture` the in-app form. The three type re-exports the file carried did not come
 * with it: `./types` declares them and the form imports them through the face.
 */

import { useState, useCallback } from 'react'
import { cn } from '@/lib/tailwind'
import type { MediaKind } from '@/entries'
import type { EnrichedMediaResult } from '@/enrichment'
import {
  type Person,
  type AlternativeResult,
  type LabelSuggestion,
  type MediaEditValues,
  MediaImage,
  MediaMetadata,
  PeopleSelector,
  LabelSuggestions,
  EditForm,
  ActionBar,
  Alternatives,
  LoadingState,
  EmptyState,
  useMediaPreviewEdit,
  getSubtitle,
} from './index'

interface MediaPreviewCardProps {
  type: MediaKind
  /** The enriched data from external API */
  enrichedData: EnrichedMediaResult | null
  /** Whether enrichment is in progress */
  isEnriching: boolean
  /** Whether save/confirm is in progress */
  isSubmitting?: boolean
  /** Error message from enrichment or submission */
  error?: string | null
  /** Callback when user confirms the selection */
  onConfirm: () => void
  /** Callback when user wants to dismiss/change */
  onDismiss: () => void
  /** Callback to retry enrichment */
  onRetry?: () => void
  /** Callback when user selects an alternative */
  onSelectAlternative?: (alternative: AlternativeResult) => void
  /** People selection */
  selectedPeople?: Person[]
  availablePeople?: Person[]
  onAddPerson?: (person: Person) => void
  onRemovePerson?: (person: Person) => void
  onAddNewPerson?: () => void
  isPeopleLoading?: boolean
  /** Label suggestions for artists (from Discogs releases) */
  labelSuggestions?: LabelSuggestion[]
  /** Callback when user toggles a label suggestion */
  onToggleLabelSuggestion?: (externalId: string) => void
  /** Whether labels are being checked */
  isCheckingLabels?: boolean
  /** Callback when user edits fields */
  onEditChange?: (values: MediaEditValues) => void
  /** Current edited values */
  editedValues?: MediaEditValues
  className?: string
}

/**
 * Preview card shown when AI detects a media entry.
 * Shows enriched data from external APIs (TMDB, OpenLibrary, Discogs).
 * Supports showing alternatives for user selection.
 */
export function MediaPreviewCard({
  type,
  enrichedData,
  isEnriching,
  isSubmitting = false,
  error,
  onConfirm,
  onDismiss,
  onRetry,
  onSelectAlternative,
  selectedPeople = [],
  availablePeople = [],
  onAddPerson,
  onRemovePerson,
  onAddNewPerson,
  isPeopleLoading = false,
  labelSuggestions = [],
  onToggleLabelSuggestion,
  isCheckingLabels = false,
  onEditChange,
  editedValues,
  className,
}: MediaPreviewCardProps) {
  const [showAlternatives, setShowAlternatives] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const enriched = enrichedData?.enriched
  const { initialEditValues } = useMediaPreviewEdit(
    type,
    enrichedData,
    editedValues,
  )

  const alternatives = enrichedData?.alternatives ?? []
  const hasAlternatives = alternatives.length > 0
  const isDisabled = isEnriching || isSubmitting

  const handleImageError = useCallback(() => {
    setImageError(true)
  }, [])

  const handleSaveEdit = (values: MediaEditValues) => {
    onEditChange?.(values)
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
  }

  // Loading state
  if (isEnriching) {
    return <LoadingState type={type} className={className} />
  }

  // No enriched data (API lookup failed or returned nothing)
  if (!enrichedData || !enriched) {
    return (
      <EmptyState
        type={type}
        name={enrichedData?.name || 'Unknown'}
        error={error}
        onRetry={onRetry}
        onDismiss={onDismiss}
        onConfirm={onConfirm}
        isSubmitting={isSubmitting}
        disabled={isDisabled}
        className={className}
      />
    )
  }

  // Has enriched data - show preview card
  const displayTitle =
    editedValues?.title ||
    editedValues?.name ||
    enriched.title ||
    enrichedData.name
  const displayGenres = editedValues?.genres || enriched.genres || []
  const displayDescription =
    editedValues?.description ||
    editedValues?.bio ||
    editedValues?.profile ||
    enriched.description
  const displayYear = editedValues?.year ?? enriched.year
  const displayDirector = editedValues?.director ?? enriched.director
  const displayAuthor = editedValues?.author ?? enriched.author
  const displayWebsite = editedValues?.website ?? enriched.website

  const title = displayTitle
  const subtitle = getSubtitle(type, {
    ...enriched,
    title: displayTitle,
    year: displayYear,
    director: displayDirector,
    author: displayAuthor,
    website: displayWebsite,
  })
  const description = displayDescription

  return (
    <div className={cn('overflow-hidden rounded-lg border', className)}>
      {isEditing ? (
        <EditForm
          type={type}
          initialValues={initialEditValues}
          onSave={handleSaveEdit}
          onCancel={handleCancelEdit}
          isSubmitting={isSubmitting}
        />
      ) : (
        <>
          <div className="flex gap-3 p-3">
            <MediaImage
              type={type}
              imageUrl={enriched.imageUrl}
              title={title}
              hasError={imageError}
              onError={handleImageError}
            />

            <MediaMetadata
              type={type}
              title={title}
              subtitle={subtitle}
              genres={displayGenres}
              description={description}
              topics={enriched.topics}
            />
          </div>

          {/* People selector - only show if handlers provided */}
          {onAddPerson && onRemovePerson && (
            <PeopleSelector
              selectedPeople={selectedPeople}
              availablePeople={availablePeople}
              onAddPerson={onAddPerson}
              onRemovePerson={onRemovePerson}
              onAddNewPerson={onAddNewPerson}
              isLoading={isPeopleLoading}
              disabled={isDisabled}
            />
          )}

          {/* Label suggestions - only for artists */}
          {type === 'artist' && onToggleLabelSuggestion && (
            <LabelSuggestions
              suggestions={labelSuggestions}
              onToggle={onToggleLabelSuggestion}
              isChecking={isCheckingLabels}
              disabled={isDisabled}
            />
          )}

          {/* Action bar */}
          <ActionBar
            type={type}
            confidence={enrichedData.confidence}
            hasAlternatives={hasAlternatives}
            alternativesCount={alternatives.length}
            showAlternatives={showAlternatives}
            onToggleAlternatives={() => setShowAlternatives(!showAlternatives)}
            onEdit={onEditChange ? () => setIsEditing(true) : undefined}
            onDismiss={onDismiss}
            onConfirm={onConfirm}
            isSubmitting={isSubmitting}
            disabled={isDisabled}
          />
        </>
      )}

      {/* Alternatives panel */}
      {showAlternatives && hasAlternatives && (
        <Alternatives
          alternatives={alternatives}
          type={type}
          onSelect={onSelectAlternative}
          disabled={isDisabled}
        />
      )}
    </div>
  )
}
